import {
    AuthenticationRequestDTO,
    AuthenticationResultDTO,
    RefreshTokenDTO,
    IdentityClient_V1,
    CompanyTypes
} from './generated/api.generated.v1';
import ApiConfig from './ApiConfig';
import ApiHelper from '../utils/ApiHelper';
import {Loading} from 'quasar';

interface UserToken {
    accessToken: string;
    refreshToken: string;
}

export default class AuthService {
    private static userToken: UserToken;
    public static readonly USER_INFO_KEY = 'USER_INFO_KEY';
    private readonly _identityClient: IdentityClient_V1;

    constructor() {
        this._identityClient = new IdentityClient_V1(new ApiConfig(), process.env.VUE_APP_API_V1_URL);
    }

    public async AcceptTermsAndConditions() : Promise<void> {
        await this._identityClient.acceptTermsAndConditions();
    }

    public async Login(username: string, password: string, companyType: CompanyTypes): Promise<AuthenticationResultDTO> {
        const request = new AuthenticationRequestDTO();
        request.userName = username;
        request.password = password;
        request.companyType = companyType;

        const response: AuthenticationResultDTO =
            await ApiHelper.ParseDTO<AuthenticationResultDTO>(() => this._identityClient.login(request));

        if (response.succeeded) {
            AuthService.SaveToken({
                accessToken: <string>response.accessToken,
                refreshToken: <string>response.refreshToken
            });
        }

        return response;
    }

    public async RefreshToken(): Promise<AuthenticationResultDTO | null> {
        const currentUser = AuthService.GetUserToken();

        if (currentUser == null || currentUser.refreshToken == null) {
            return null;
        }

        if (!Loading.isActive) {
            Loading.show();
        }

        const request = new RefreshTokenDTO();
        request.refreshToken = currentUser.refreshToken;
        request.expiredAccessToken = currentUser.accessToken;

        const response = await this._identityClient.refreshToken(request);

        AuthService.SaveToken({
            accessToken: <string>response.accessToken,
            refreshToken: <string>response.refreshToken
        });

        Loading.hide();

        return response;
    }

    public static SaveToken(userToken: UserToken) {
        this.userToken = userToken;
        localStorage.setItem(this.USER_INFO_KEY, JSON.stringify(userToken));
    }

    public static RemoveToken() {
        localStorage.removeItem(this.USER_INFO_KEY);
    }

    public static GetUserToken(): UserToken | null {
        const inMemory = this.userToken;

        if (inMemory) return inMemory;

        const inStorage = localStorage.getItem(this.USER_INFO_KEY);

        if (inStorage) {
            return JSON.parse(inStorage) as UserToken;
        }

        return null;
    }

    public static GetToken(): string {
        return this.GetUserToken()?.accessToken || '';
    }

    async Logout() {
        const currentToken = AuthService.GetUserToken();

        if (currentToken == null || currentToken.refreshToken == null) return;

        AuthService.RemoveToken();

        try {
            const request = new RefreshTokenDTO();
            request.refreshToken = currentToken.refreshToken;
    
            await this._identityClient.revokeToken(request);
        } catch(e) {}        
    }
}